import { useState } from 'react';
import {
  Box,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import InsertLinkRoundedIcon from '@mui/icons-material/InsertLinkRounded';
import CodeRoundedIcon from '@mui/icons-material/CodeRounded';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import CodeBlock from '../global/CodeBlock';
import { CUSTOMER_WEB_URL } from '../../constants/global';

function ShareCard({
  icon,
  title,
  hint,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  hint: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Stack
      sx={{
        border: theme => `1px solid ${theme.palette.lightOutlineGrey.main}`,
        borderRadius: 2,
        overflow: 'hidden',
      }}>
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        sx={{
          px: 2,
          py: 1.25,
          bgcolor: theme => theme.palette.lightGrey.main,
          borderBottom: theme =>
            `1px solid ${theme.palette.lightOutlineGrey.main}`,
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: theme => theme.palette.text.secondary,
            typography: 'body2',
          }}>
          {icon}
        </Box>
        <Typography variant="h5">{title}</Typography>
        <Stack sx={{ ml: 'auto', flexShrink: 0 }} justifyContent="center">
          {hint}
        </Stack>
      </Stack>
      {children}
    </Stack>
  );
}

interface Props {
  shopUrl: string;
}

export default function GiftCardStoreShare({ shopUrl }: Props) {
  const [copiedLink, setCopiedLink] = useState(false);

  const storeUrl = `${CUSTOMER_WEB_URL}/shop/${shopUrl}/gift-cards`;
  const embedCode = `<iframe src="${storeUrl}/embed?buttonBackgroundColor=black&buttonTextColor=white" width="100%" height="600" frameborder="0" />`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(storeUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  return (
    <Stack gap={2.5} mt={1}>
      <Divider />
      <Stack gap={0.5}>
        <Typography variant="h5">Share your gift card store</Typography>
        <Typography
          variant="body2"
          sx={{ color: theme => theme.palette.text.secondary }}>
          Let customers purchase gift cards directly online — share the link or
          embed the store on your website.
        </Typography>
      </Stack>

      <ShareCard
        icon={<InsertLinkRoundedIcon sx={{ fontSize: 14 }} />}
        title="Direct link"
        hint={
          <Typography
            variant="caption"
            sx={{ color: theme => theme.palette.text.secondary }}>
            Share on social, email, or anywhere
          </Typography>
        }>
        <Stack
          direction="row"
          alignItems="center"
          gap={0.5}
          sx={{ px: 1.5, py: 1 }}>
          <Typography
            variant="caption"
            sx={{
              flex: 1,
              color: theme => theme.palette.text.secondary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              userSelect: 'all',
            }}>
            {storeUrl}
          </Typography>
          <Tooltip title={copiedLink ? 'Copied!' : 'Copy link'} placement="top">
            <IconButton size="small" onClick={handleCopyLink}>
              {copiedLink ? (
                <CheckIcon
                  sx={{
                    fontSize: 14,
                    color: theme => theme.palette.success.main,
                  }}
                />
              ) : (
                <ContentCopyIcon
                  sx={{
                    fontSize: 14,
                    color: theme => theme.palette.text.secondary,
                  }}
                />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Open in new tab" placement="top">
            <IconButton
              size="small"
              component="a"
              href={storeUrl}
              target="_blank"
              rel="noopener noreferrer">
              <OpenInNewRoundedIcon
                sx={{
                  fontSize: 14,
                  color: theme => theme.palette.text.secondary,
                }}
              />
            </IconButton>
          </Tooltip>
        </Stack>
      </ShareCard>

      <ShareCard
        icon={<CodeRoundedIcon sx={{ fontSize: 14 }} />}
        title="Embed on your website"
        hint={
          <Typography
            component="a"
            href={`${storeUrl}/embed-demo`}
            target="_blank"
            rel="noopener noreferrer"
            variant="caption"
            sx={{
              color: theme => theme.palette.primary.main,
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}>
            See an example
          </Typography>
        }>
        <Typography
          variant="caption"
          component="p"
          sx={{ px: 2, pt: 1.5, color: theme => theme.palette.text.secondary }}>
          Paste this snippet into your website&apos;s HTML where you&apos;d like
          the gift card store to appear.
        </Typography>
        <Box sx={{ px: 1.5, pb: 1.5, pt: 1 }}>
          <CodeBlock code={embedCode} language="html" />
        </Box>
      </ShareCard>
    </Stack>
  );
}
